import { c as _c } from "react/compiler-runtime";
import { useRouter } from "next/router";
import Link from "next/link";
import { useCallback, useState, useEffect, useRef } from "react";
import type { MessageKey, SupportedLocale } from "@/lib/i18n";
import { t } from "@/lib/i18n";
import type { ProductBrand, ProductCategory, MilitaryFaction, MilitarySubcategory } from "@/data/products";
import { CATEGORIES, MILITARY_FACTIONS, MILITARY_SUBCATEGORIES, PRODUCT_BRANDS, getUniqueScales, getPiecesRange } from "@/data/products";
import { getAllProductsUrl, getCategoryUrl, getMilitarySubcategoryUrl } from "@/lib/urls";
import styles from "@/styles/ProductsSidebar.module.css";

/** Implicit `toLocaleString()` uses server OS vs browser locale → hydration mismatch. */
const NUMBER_FORMAT_LOCALE: Record<SupportedLocale, string> = {
  en: "en-US",
  pl: "pl-PL",
  es: "es-ES",
  de: "de-DE",
  fr: "fr-FR",
  nl: "nl-NL",
  zh: "zh-CN",
  it: "it-IT",
  ru: "ru-RU"
};
function formatPieceCount(n: number, locale: SupportedLocale): string {
  return n.toLocaleString(NUMBER_FORMAT_LOCALE[locale]);
}
interface ProductsSidebarProps {
  locale: SupportedLocale;
  searchQuery?: string | null;
  brands?: ProductBrand[] | null;
  category?: ProductCategory | null;
  subcategory?: MilitarySubcategory | null;
  factions?: MilitaryFaction[] | null;
  scales?: string[] | null;
  piecesMin?: number | null;
  piecesMax?: number | null;
}
function buildProductsQuery(params: Record<string, string | number | undefined | null>) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== "" && v !== "all") {
      q.set(k, String(v));
    }
  }
  return q.toString();
}
export function ProductsSidebar(t0) {
  const $ = _c(180);
  const {
    locale,
    searchQuery: t1,
    brands: t2,
    category,
    subcategory,
    factions: t3,
    scales: t4,
    piecesMin,
    piecesMax
  } = t0;
  const searchQuery = t1 === undefined ? "" : t1;
  let t5;
  if ($[0] !== t2) {
    t5 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const brands = t5;
  let t6;
  if ($[2] !== t3) {
    t6 = t3 === undefined ? [] : t3;
    $[2] = t3;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const factions = t6;
  let t7;
  if ($[4] !== t4) {
    t7 = t4 === undefined ? [] : t4;
    $[4] = t4;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const scales = t7;
  const router = useRouter();
  let t8;
  if ($[6] !== category || $[7] !== locale || $[8] !== subcategory) {
    t8 = category && subcategory ? getMilitarySubcategoryUrl(subcategory, locale) : category ? getCategoryUrl(category, locale) : getAllProductsUrl(locale);
    $[6] = category;
    $[7] = locale;
    $[8] = subcategory;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const basePath = t8;
  let t9;
  if ($[10] !== router.query) {
    t9 = {
      ...router.query
    };
    $[10] = router.query;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const query = t9;
  const sortRaw = query.sort;
  const sort = typeof sortRaw === "string" ? sortRaw : undefined;
  let t10;
  if ($[12] !== basePath || $[13] !== brands || $[14] !== category || $[15] !== factions || $[16] !== piecesMax || $[17] !== piecesMin || $[18] !== scales || $[19] !== searchQuery || $[20] !== sort || $[21] !== subcategory) {
    t10 = overrides => {
      const merged = {
        q: searchQuery && searchQuery.trim() ? searchQuery.trim() : undefined,
        brands: Array.isArray(brands) && brands.length > 0 ? brands.join(",") : undefined,
        category: category || undefined,
        subcategory: subcategory || undefined,
        faction: Array.isArray(factions) && factions.length > 0 ? factions.join(",") : undefined,
        scale: Array.isArray(scales) && scales.length > 0 ? scales.join(",") : undefined,
        piecesMin: piecesMin ?? undefined,
        piecesMax: piecesMax ?? undefined,
        page: 1,
        sort: sort || undefined,
        ...overrides
      };
      const q = buildProductsQuery(merged);
      return q ? `${basePath}?${q}` : basePath;
    };
    $[12] = basePath;
    $[13] = brands;
    $[14] = category;
    $[15] = factions;
    $[16] = piecesMax;
    $[17] = piecesMin;
    $[18] = scales;
    $[19] = searchQuery;
    $[20] = sort;
    $[21] = subcategory;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const buildUrl = t10;
  let t11;
  if ($[23] !== brands || $[24] !== buildUrl || $[25] !== router) {
    t11 = (brand, checked) => {
      const current = Array.isArray(brands) ? [...brands] : [];
      const next = checked ? [...current, brand] : current.filter(b => b !== brand);
      const brandsParam = next.length > 0 ? next.join(",") : null;
      router.push(buildUrl({
        brands: brandsParam ?? undefined
      }), undefined, {
        scroll: false
      });
    };
    $[23] = brands;
    $[24] = buildUrl;
    $[25] = router;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const handleBrandToggle = t11;
  let t12;
  if ($[27] !== buildUrl || $[28] !== factions || $[29] !== router) {
    t12 = (f, checked_0) => {
      const current_0 = Array.isArray(factions) ? [...factions] : [];
      const next_0 = checked_0 ? [...current_0, f] : current_0.filter(x => x !== f);
      const factionParam = next_0.length > 0 ? next_0.join(",") : null;
      router.push(buildUrl({
        faction: factionParam ?? undefined
      }), undefined, {
        scroll: false
      });
    };
    $[27] = buildUrl;
    $[28] = factions;
    $[29] = router;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const handleFactionToggle = t12;
  let t13;
  if ($[31] !== buildUrl || $[32] !== router || $[33] !== scales) {
    t13 = (scaleVal, checked_1) => {
      const current_1 = Array.isArray(scales) ? [...scales] : [];
      const next_1 = checked_1 ? [...current_1, scaleVal] : current_1.filter(s => s !== scaleVal);
      const scaleParam = next_1.length > 0 ? next_1.join(",") : null;
      router.push(buildUrl({
        scale: scaleParam ?? undefined
      }), undefined, {
        scroll: false
      });
    };
    $[31] = buildUrl;
    $[32] = router;
    $[33] = scales;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const handleScaleToggle = t13;
  let t14;
  if ($[35] !== buildUrl || $[36] !== router) {
    t14 = (newMin, newMax) => {
      const {
        min: dataMin,
        max: dataMax
      } = getPiecesRange();
      const atFullRange = newMin <= dataMin && newMax >= dataMax;
      const minVal = atFullRange || newMin <= dataMin ? undefined : newMin;
      const maxVal = atFullRange || newMax >= dataMax ? undefined : newMax;
      router.push(buildUrl({
        piecesMin: minVal,
        piecesMax: maxVal
      }), undefined, {
        scroll: false
      });
    };
    $[35] = buildUrl;
    $[36] = router;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  const handlePiecesChange = t14;
  const [searchInput, setSearchInput] = useState(searchQuery ?? "");
  let t15;
  let t16;
  if ($[38] !== searchQuery) {
    t15 = () => {
      setSearchInput(searchQuery ?? "");
    };
    t16 = [searchQuery];
    $[38] = searchQuery;
    $[39] = t15;
    $[40] = t16;
  } else {
    t15 = $[39];
    t16 = $[40];
  }
  useEffect(t15, t16);
  let t17;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = value => {
      setSearchInput(value);
    };
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  const handleSearchChange = t17;
  let t18;
  if ($[42] !== buildUrl || $[43] !== router || $[44] !== searchInput) {
    t18 = () => {
      const trimmed = searchInput.trim();
      router.push(buildUrl({
        q: trimmed || undefined
      }), undefined, {
        scroll: false
      });
    };
    $[42] = buildUrl;
    $[43] = router;
    $[44] = searchInput;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  const handleSearchSubmit = t18;
  let effectiveMax;
  let effectiveMin;
  let piecesRange;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  if ($[46] !== brands || $[47] !== category || $[48] !== factions || $[49] !== handleBrandToggle || $[50] !== handleFactionToggle || $[51] !== handleScaleToggle || $[52] !== handleSearchSubmit || $[53] !== locale || $[54] !== piecesMax || $[55] !== piecesMin || $[56] !== scales || $[57] !== searchInput || $[58] !== subcategory) {
    const scalesList = getUniqueScales();
    let t26;
    if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
      t26 = getPiecesRange();
      $[69] = t26;
    } else {
      t26 = $[69];
    }
    piecesRange = t26;
    effectiveMin = piecesMin ?? piecesRange.min;
    effectiveMax = piecesMax ?? piecesRange.max;
    t19 = styles.sidebar;
    let t27;
    if ($[70] !== locale) {
      t27 = t(locale, "products.filters.title");
      $[70] = locale;
      $[71] = t27;
    } else {
      t27 = $[71];
    }
    if ($[72] !== t27) {
      t20 = <h3 className={styles.sidebarTitle}>{t27}</h3>;
      $[72] = t27;
      $[73] = t20;
    } else {
      t20 = $[73];
    }
    let t28;
    if ($[74] !== locale) {
      t28 = t(locale, "products.filters.search");
      $[74] = locale;
      $[75] = t28;
    } else {
      t28 = $[75];
    }
    let t29;
    if ($[76] !== t28) {
      t29 = <h4 className={styles.filterHeading}>{t28}</h4>;
      $[76] = t28;
      $[77] = t29;
    } else {
      t29 = $[77];
    }
    let t30;
    if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = e => handleSearchChange(e.target.value);
      $[78] = t30;
    } else {
      t30 = $[78];
    }
    let t31;
    if ($[79] !== handleSearchSubmit) {
      t31 = e_0 => e_0.key === "Enter" && handleSearchSubmit();
      $[79] = handleSearchSubmit;
      $[80] = t31;
    } else {
      t31 = $[80];
    }
    let t32;
    if ($[81] !== locale) {
      t32 = t(locale, "products.filters.searchPlaceholder");
      $[81] = locale;
      $[82] = t32;
    } else {
      t32 = $[82];
    }
    let t33;
    if ($[83] !== locale) {
      t33 = t(locale, "products.filters.search");
      $[83] = locale;
      $[84] = t33;
    } else {
      t33 = $[84];
    }
    let t34;
    if ($[85] !== handleSearchSubmit || $[86] !== searchInput || $[87] !== t31 || $[88] !== t32 || $[89] !== t33) {
      t34 = <input type="search" value={searchInput} onChange={t30} onKeyDown={t31} onBlur={handleSearchSubmit} placeholder={t32} className={styles.searchInput} aria-label={t33} />;
      $[85] = handleSearchSubmit;
      $[86] = searchInput;
      $[87] = t31;
      $[88] = t32;
      $[89] = t33;
      $[90] = t34;
    } else {
      t34 = $[90];
    }
    let t35;
    if ($[91] !== locale) {
      t35 = t(locale, "products.filters.search");
      $[91] = locale;
      $[92] = t35;
    } else {
      t35 = $[92];
    }
    let t36;
    if ($[93] !== locale) {
      t36 = t(locale, "nav.search");
      $[93] = locale;
      $[94] = t36;
    } else {
      t36 = $[94];
    }
    let t37;
    if ($[95] !== handleSearchSubmit || $[96] !== t35 || $[97] !== t36) {
      t37 = <button type="button" onClick={handleSearchSubmit} className={styles.searchBtn} aria-label={t35}>{t36}</button>;
      $[95] = handleSearchSubmit;
      $[96] = t35;
      $[97] = t36;
      $[98] = t37;
    } else {
      t37 = $[98];
    }
    let t38;
    if ($[99] !== t34 || $[100] !== t37) {
      t38 = <div className={styles.searchWrap}>{t34}{t37}</div>;
      $[99] = t34;
      $[100] = t37;
      $[101] = t38;
    } else {
      t38 = $[101];
    }
    if ($[102] !== t29 || $[103] !== t38) {
      t21 = <div className={styles.filterBlock}>{t29}{t38}</div>;
      $[102] = t29;
      $[103] = t38;
      $[104] = t21;
    } else {
      t21 = $[104];
    }
    let t39;
    if ($[105] !== locale) {
      t39 = t(locale, "products.filters.brand");
      $[105] = locale;
      $[106] = t39;
    } else {
      t39 = $[106];
    }
    let t40;
    if ($[107] !== t39) {
      t40 = <h4 className={styles.filterHeading}>{t39}</h4>;
      $[107] = t39;
      $[108] = t40;
    } else {
      t40 = $[108];
    }
    let t41;
    if ($[109] !== brands || $[110] !== handleBrandToggle || $[111] !== locale) {
      t41 = PRODUCT_BRANDS.map(brand_0 => {
        const isChecked = Array.isArray(brands) && brands.includes(brand_0);
        return <label key={brand_0} className={styles.checkboxLabel}><input type="checkbox" checked={!!isChecked} onChange={e_1 => handleBrandToggle(brand_0, e_1.target.checked)} className={styles.checkbox} /><span>{t(locale, `brand.${brand_0}`)}</span></label>;
      });
      $[109] = brands;
      $[110] = handleBrandToggle;
      $[111] = locale;
      $[112] = t41;
    } else {
      t41 = $[112];
    }
    let t42;
    if ($[113] !== t41) {
      t42 = <div className={styles.filterCheckboxes}>{t41}</div>;
      $[113] = t41;
      $[114] = t42;
    } else {
      t42 = $[114];
    }
    if ($[115] !== t40 || $[116] !== t42) {
      t22 = <div className={styles.filterBlock}>{t40}{t42}</div>;
      $[115] = t40;
      $[116] = t42;
      $[117] = t22;
    } else {
      t22 = $[117];
    }
    let t43;
    if ($[118] !== locale) {
      t43 = t(locale, "products.filters.category");
      $[118] = locale;
      $[119] = t43;
    } else {
      t43 = $[119];
    }
    let t44;
    if ($[120] !== t43) {
      t44 = <h4 className={styles.filterHeading}>{t43}</h4>;
      $[120] = t43;
      $[121] = t44;
    } else {
      t44 = $[121];
    }
    let t45;
    if ($[122] !== locale) {
      t45 = t(locale, "products.filters.category");
      $[122] = locale;
      $[123] = t45;
    } else {
      t45 = $[123];
    }
    let t46;
    if ($[124] !== category || $[125] !== locale || $[126] !== subcategory) {
      t46 = CATEGORIES.map(c => <li key={c} className={styles.treeItem}><Link href={getCategoryUrl(c, locale)} locale={locale} className={`${styles.treeLink} ${styles.treeLinkParent} ${category === c && !subcategory ? styles.filterLinkActive : ""}`}>{t(locale, `category.${c}`)}</Link>{c === "military" && <ul className={styles.treeChildren}>{MILITARY_SUBCATEGORIES.map(sc => <li key={sc}><Link href={getMilitarySubcategoryUrl(sc, locale)} locale={locale} className={`${styles.treeLink} ${styles.treeLinkChild} ${subcategory === sc ? styles.filterLinkActive : ""}`}>{t(locale, `militarySub.${sc}`)}</Link></li>)}</ul>}</li>);
      $[124] = category;
      $[125] = locale;
      $[126] = subcategory;
      $[127] = t46;
    } else {
      t46 = $[127];
    }
    let t47;
    if ($[128] !== t46) {
      t47 = <ul className={styles.treeList}>{t46}</ul>;
      $[128] = t46;
      $[129] = t47;
    } else {
      t47 = $[129];
    }
    let t48;
    if ($[130] !== t45 || $[131] !== t47) {
      t48 = <nav className={styles.categoryTree} aria-label={t45}>{t47}</nav>;
      $[130] = t45;
      $[131] = t47;
      $[132] = t48;
    } else {
      t48 = $[132];
    }
    if ($[133] !== t44 || $[134] !== t48) {
      t23 = <div className={styles.filterBlock}>{t44}{t48}</div>;
      $[133] = t44;
      $[134] = t48;
      $[135] = t23;
    } else {
      t23 = $[135];
    }
    if ($[136] !== category || $[137] !== factions || $[138] !== handleFactionToggle || $[139] !== locale) {
      t24 = category === "military" && <div className={styles.filterBlock}><h4 className={styles.filterHeading}>{t(locale, "products.factionAll")}</h4><div className={styles.filterCheckboxes}>{MILITARY_FACTIONS.map(f_0 => {
            const isChecked_0 = Array.isArray(factions) && factions.includes(f_0);
            return <label key={f_0} className={styles.checkboxLabel}><input type="checkbox" checked={!!isChecked_0} onChange={e_2 => handleFactionToggle(f_0, e_2.target.checked)} className={styles.checkbox} /><span>{t(locale, `faction.${f_0}`)}</span></label>;
          })}</div></div>;
      $[136] = category;
      $[137] = factions;
      $[138] = handleFactionToggle;
      $[139] = locale;
      $[140] = t24;
    } else {
      t24 = $[140];
    }
    t25 = scalesList.length > 0 && <div className={styles.filterBlock}><h4 className={styles.filterHeading}>{t(locale, "products.filters.scale")}</h4><div className={styles.filterCheckboxes}>{scalesList.map(s_0 => {
          const isChecked_1 = Array.isArray(scales) && scales.includes(s_0);
          return <label key={s_0} className={styles.checkboxLabel}><input type="checkbox" checked={!!isChecked_1} onChange={e_3 => handleScaleToggle(s_0, e_3.target.checked)} className={styles.checkbox} /><span>{s_0}</span></label>;
        })}</div></div>;
    $[46] = brands;
    $[47] = category;
    $[48] = factions;
    $[49] = handleBrandToggle;
    $[50] = handleFactionToggle;
    $[51] = handleScaleToggle;
    $[52] = handleSearchSubmit;
    $[53] = locale;
    $[54] = piecesMax;
    $[55] = piecesMin;
    $[56] = scales;
    $[57] = searchInput;
    $[58] = subcategory;
    $[59] = effectiveMax;
    $[60] = effectiveMin;
    $[61] = piecesRange;
    $[62] = t19;
    $[63] = t20;
    $[64] = t21;
    $[65] = t22;
    $[66] = t23;
    $[67] = t24;
    $[68] = t25;
  } else {
    effectiveMax = $[59];
    effectiveMin = $[60];
    piecesRange = $[61];
    t19 = $[62];
    t20 = $[63];
    t21 = $[64];
    t22 = $[65];
    t23 = $[66];
    t24 = $[67];
    t25 = $[68];
  }
  let t26;
  if ($[141] !== locale) {
    t26 = t(locale, "products.filters.pieces");
    $[141] = locale;
    $[142] = t26;
  } else {
    t26 = $[142];
  }
  let t27;
  if ($[143] !== t26) {
    t27 = <h4 className={styles.filterHeading}>{t26}</h4>;
    $[143] = t26;
    $[144] = t27;
  } else {
    t27 = $[144];
  }
  let t28;
  if ($[145] !== effectiveMin || $[146] !== locale) {
    t28 = formatPieceCount(effectiveMin, locale);
    $[145] = effectiveMin;
    $[146] = locale;
    $[147] = t28;
  } else {
    t28 = $[147];
  }
  let t29;
  if ($[148] !== t28) {
    t29 = <span className={styles.piecesValue}>{t28}</span>;
    $[148] = t28;
    $[149] = t29;
  } else {
    t29 = $[149];
  }
  let t30;
  if ($[150] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <span className={styles.piecesSeparator}>–</span>;
    $[150] = t30;
  } else {
    t30 = $[150];
  }
  let t31;
  if ($[151] !== effectiveMax || $[152] !== locale) {
    t31 = formatPieceCount(effectiveMax, locale);
    $[151] = effectiveMax;
    $[152] = locale;
    $[153] = t31;
  } else {
    t31 = $[153];
  }
  let t32;
  if ($[154] !== t31) {
    t32 = <span className={styles.piecesValue}>{t31}</span>;
    $[154] = t31;
    $[155] = t32;
  } else {
    t32 = $[155];
  }
  let t33;
  if ($[156] !== t29 || $[157] !== t32) {
    t33 = <div className={styles.piecesValues}>{t29}{t30}{t32}</div>;
    $[156] = t29;
    $[157] = t32;
    $[158] = t33;
  } else {
    t33 = $[158];
  }
  let t34;
  if ($[159] !== t27 || $[160] !== t33) {
    t34 = <div className={styles.piecesFilterHeader}>{t27}{t33}</div>;
    $[159] = t27;
    $[160] = t33;
    $[161] = t34;
  } else {
    t34 = $[161];
  }
  let t35;
  if ($[162] !== effectiveMax || $[163] !== effectiveMin || $[164] !== handlePiecesChange || $[165] !== piecesRange.max || $[166] !== piecesRange.min) {
    t35 = <div className={styles.rangeSliderWrapper}><PiecesRangeSlider min={piecesRange.min} max={piecesRange.max} valueMin={effectiveMin} valueMax={effectiveMax} onChange={handlePiecesChange} /></div>;
    $[162] = effectiveMax;
    $[163] = effectiveMin;
    $[164] = handlePiecesChange;
    $[165] = piecesRange.max;
    $[166] = piecesRange.min;
    $[167] = t35;
  } else {
    t35 = $[167];
  }
  let t36;
  if ($[168] !== t34 || $[169] !== t35) {
    t36 = <div className={styles.filterBlock}>{t34}{t35}</div>;
    $[168] = t34;
    $[169] = t35;
    $[170] = t36;
  } else {
    t36 = $[170];
  }
  let t37;
  if ($[171] !== t19 || $[172] !== t20 || $[173] !== t21 || $[174] !== t22 || $[175] !== t23 || $[176] !== t24 || $[177] !== t25 || $[178] !== t36) {
    t37 = <aside className={t19}>{t20}{t21}{t22}{t23}{t24}{t25}{t36}</aside>;
    $[171] = t19;
    $[172] = t20;
    $[173] = t21;
    $[174] = t22;
    $[175] = t23;
    $[176] = t24;
    $[177] = t25;
    $[178] = t36;
    $[179] = t37;
  } else {
    t37 = $[179];
  }
  return t37;
}
interface PiecesRangeSliderProps {
  min: number;
  max: number;
  valueMin: number;
  valueMax: number;
  onChange: (min: number, max: number) => void;
}
function PiecesRangeSlider({
  min,
  max,
  valueMin,
  valueMax,
  onChange
}: PiecesRangeSliderProps) {
  const range = max - min;
  const step = Math.max(1, Math.floor(range / 300));
  const [isDragging, setIsDragging] = useState(false);
  const [localMin, setLocalMin] = useState(valueMin);
  const [localMax, setLocalMax] = useState(valueMax);
  const [waitingForProps, setWaitingForProps] = useState(false);
  const localMinRef = useRef(valueMin);
  const localMaxRef = useRef(valueMax);
  const isDraggingRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    localMinRef.current = localMin;
    localMaxRef.current = localMax;
  }, [localMin, localMax]);
  useEffect(() => {
    isDraggingRef.current = isDragging;
  }, [isDragging]);
  useEffect(() => {
    if (!isDragging) {
      if (waitingForProps) {
        if (valueMin === localMinRef.current && valueMax === localMaxRef.current) {
          setWaitingForProps(false);
          setLocalMin(valueMin);
          setLocalMax(valueMax);
        }
        return;
      }
      setLocalMin(valueMin);
      setLocalMax(valueMax);
    }
  }, [valueMin, valueMax, isDragging, waitingForProps]);
  useEffect(() => {
    const commit = () => {
      if (!isDraggingRef.current) return;
      const minVal = localMinRef.current;
      const maxVal = localMaxRef.current;
      onChangeRef.current(minVal, maxVal);
      isDraggingRef.current = false;
      setIsDragging(false);
      setWaitingForProps(true);
    };
    document.addEventListener("mouseup", commit);
    document.addEventListener("touchend", commit);
    return () => {
      document.removeEventListener("mouseup", commit);
      document.removeEventListener("touchend", commit);
    };
  }, []);
  const displayMin = isDragging || waitingForProps ? localMin : valueMin;
  const displayMax = isDragging || waitingForProps ? localMax : valueMax;
  const lowPct = (displayMin - min) / range * 100;
  const highPct = (displayMax - min) / range * 100;
  const startDrag = () => {
    isDraggingRef.current = true;
    setIsDragging(true);
    setLocalMin(valueMin);
    setLocalMax(valueMax);
    localMinRef.current = valueMin;
    localMaxRef.current = valueMax;
  };
  const handleLowChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isDraggingRef.current) {
      isDraggingRef.current = true;
      setIsDragging(true);
      setLocalMin(valueMin);
      setLocalMax(valueMax);
      localMinRef.current = valueMin;
      localMaxRef.current = valueMax;
    }
    const v = Math.min(Number(e.target.value), localMaxRef.current);
    setLocalMin(v);
    localMinRef.current = v;
  };
  const handleHighChange = (e_0: React.ChangeEvent<HTMLInputElement>) => {
    if (!isDraggingRef.current) {
      isDraggingRef.current = true;
      setIsDragging(true);
      setLocalMin(valueMin);
      setLocalMax(valueMax);
      localMinRef.current = valueMin;
      localMaxRef.current = valueMax;
    }
    const v_0 = Math.max(Number(e_0.target.value), localMinRef.current);
    setLocalMax(v_0);
    localMaxRef.current = v_0;
  };
  return <div className={styles.rangeSlider}>
      <div className={styles.rangeTrackActive} style={{
      left: `${lowPct}%`,
      width: `${highPct - lowPct}%`
    }} />
      <input type="range" min={min} max={max} step={step} value={displayMin} onChange={handleLowChange} onMouseDown={startDrag} onTouchStart={startDrag} className={styles.rangeInput} aria-label="Min pieces" />
      <input type="range" min={min} max={max} step={step} value={displayMax} onChange={handleHighChange} onMouseDown={startDrag} onTouchStart={startDrag} className={`${styles.rangeInput} ${styles.rangeInputHigh}`} aria-label="Max pieces" />
    </div>;
}